/**
 * Departures — flat list of CONFIRMED EPC transactions. Each row is
 * one tag the camera+reconciler agreed left a smartbox. Pulls
 * confirmed EPCs from BOTH fully-CONFIRMED and PARTIAL parent
 * tickets (a PARTIAL ticket still has real confirmed takes; only its
 * low-confidence lines are VOID). VOID lines are excluded entirely.
 *
 * Click any row to open the parent ticket's detail modal — useful
 * when an operator wants to see the camera-episode context for a
 * suspicious tag.
 *
 * Visibility: all authenticated roles; server scopes by SCU
 * access. Vendor sessions additionally see only EPCs whose item
 * belongs to their catalog.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import DepartureEpisodeModal from "../components/DepartureEpisodeModal";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useScopedBoxes } from "../hooks/useScopedBoxes";
import { fmtAgo, fmtMoney, fmtUtc, fmtUtcInTz, fmtWorkCenter, shortScuName, tzFromLatLon } from "../lib/format";
import {
  type DepartureConfidence,
  type DepartureDetailResponse,
  type DepartureEpcRow,
  type DepartureStatus,
  fetchDeparture,
  listDepartureEpcs,
} from "../services/departuresApi";

const STATUS_PILL: Record<DepartureStatus, string> = {
  CONFIRMED: "bg-success-100 text-success-700",
  PARTIAL:   "bg-warning-100 text-warning-700",
  VOID:      "bg-secondary-200 text-secondary-600",
};

const CONFIDENCE_PILL: Record<DepartureConfidence, string> = {
  HIGH:   "bg-success-100 text-success-700",
  MEDIUM: "bg-warning-100 text-warning-700",
  LOW:    "bg-error-100 text-error-700",
};

// Month-to-date default range for the Since/Until pickers — operators land
// on the page expecting "this month's takes", not the page-1-of-N firehose.
function mtdDefaults(): { since: string; until: string } {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return { since: `${yyyy}-${mm}-01`, until: `${yyyy}-${mm}-${dd}` };
}

export const DeparturesPage: React.FC = () => {
  const { token, role, can } = useAuth();
  // Confidence is an operator triage signal (HIGH/MEDIUM/LOW + pre/miss
  // counts) — customers and vendors don't need it. Hide the column +
  // filter + body cell for non-platform users (Jason 2026-06-16).
  const showConfidence = role === "platform";
  // Price is hidden from users without pricing.view (the server also nulls
  // unit_price/currency for them). Hide the whole column so layout stays clean.
  const showPricing = can("pricing.view");
  const { snapshot: boxSnapshot } = useScopedBoxes();
  const [rows, setRows] = useState<DepartureEpcRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters — default to month-to-date so the page lands on a useful window
  // without operators having to set it every visit.
  const initialDates = useMemo(() => mtdDefaults(), []);
  // Free-text search across smartbox_code + work_center + scu_name
  // (case-insensitive substring). Applied client-side over the loaded
  // MTD set so a single input handles both "SB071" and "ND Yard".
  const [searchFilter, setSearchFilter] = useState("");
  const [sinceFilter, setSinceFilter] = useState<string>(initialDates.since);
  const [untilFilter, setUntilFilter] = useState<string>(initialDates.until);

  // Per-column refinements (Jason 2026-06-11). Stack on top of the
  // top-bar smartbox/work-center search + the date range. The
  // SmartBox and Timestamp columns don't get their own filter (search
  // bar handles smartbox; date range handles timestamp).
  const [eventFilter, setEventFilter] = useState("");
  const [itemFilter, setItemFilter] = useState("");
  const [epcFilter, setEpcFilter] = useState("");
  const [confidenceFilter, setConfidenceFilter] = useState<"" | DepartureConfidence>("");

  // Server-side EPC search (Jason 2026-06-16). Separate from the column
  // filter above — when set, the server fetches the EPC's full history
  // ignoring the date range. Useful for "what happened to this tag?"
  // lookups across months. Cleared via the X button.
  const [epcSearch, setEpcSearch] = useState("");

  // Pagination — a page of 200 covers the default MTD window in one fetch
  // (real-world counts run a few hundred per month per customer). Smaller
  // pages keep the client-side refine filters snappy. Pagination kicks in
  // when an operator widens the window past 200 records.
  const [offset, setOffset] = useState(0);
  const LIMIT = 200;

  // scu_name -> [lat, lon] from the boxes snapshot. Used to render each
  // row's "When" in the smartbox's LOCAL time (per Jason 2026-06-10) —
  // default Central, US longitude bands otherwise. NYC-metro fixes (a
  // known bogus Peplink reading) are also pinned to Central.
  const scuCoords = useMemo(() => {
    const m = new Map<string, readonly [number | null, number | null]>();
    for (const b of boxSnapshot?.boxes ?? []) {
      m.set(b.scu_name, [b.lat, b.lon] as const);
    }
    return m;
  }, [boxSnapshot]);

  // Drill-down modal — full ticket context for the row the operator clicked.
  const [detail, setDetail] = useState<DepartureDetailResponse | null>(null);
  const [detailLoadingId, setDetailLoadingId] = useState<number | null>(null);
  // Per-episode photo modal — opened by clicking the row thumbnail.
  // Carries the camera_person_episode_id + window so the modal can
  // fetch only that episode's photos without touching the parent
  // ticket-detail flow.
  const [episodeView, setEpisodeView] = useState<DepartureEpcRow | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const trimmedEpc = epcSearch.trim();
      const p = await listDepartureEpcs(token, {
        // Top-level EPC search ignores the date range (server side too).
        since: trimmedEpc ? undefined : sinceFilter ? `${sinceFilter}T00:00:00Z` : undefined,
        until: trimmedEpc ? undefined : untilFilter ? `${untilFilter}T23:59:59Z` : undefined,
        epc: trimmedEpc || undefined,
        limit: LIMIT,
        offset,
      });
      setRows(p.data);
      setTotal(p.pagination.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [token, sinceFilter, untilFilter, offset, epcSearch]);

  useEffect(() => {
    void load();
  }, [load]);

  // Reset paging when DATE filters change (the search bar filters
  // client-side, so it doesn't affect server paging).
  useEffect(() => {
    setOffset(0);
  }, [sinceFilter, untilFilter]);

  // Apply the combo Smartbox/Work-center search + per-column filters
  // client-side over the loaded MTD window. Search bar matches
  // smartbox_code / work_center / scu_name; column filters refine
  // Event (camera_person_episode_id), Item (sku/name/vendor), EPC,
  // and Confidence.
  const visibleRows = useMemo(() => {
    const q = searchFilter.trim().toLowerCase();
    const ev = eventFilter.trim().toLowerCase();
    const it = itemFilter.trim().toLowerCase();
    const ep = epcFilter.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !(
        (r.smartbox_code ?? "").toLowerCase().includes(q) ||
        (r.work_center ?? "").toLowerCase().includes(q) ||
        r.scu_name.toLowerCase().includes(q)
      )) return false;
      if (ev && !String(r.camera_person_episode_id ?? "").toLowerCase().includes(ev)) {
        return false;
      }
      if (it && !(
        (r.sku ?? "").toLowerCase().includes(it) ||
        (r.item_name ?? "").toLowerCase().includes(it) ||
        (r.vendor_name ?? "").toLowerCase().includes(it) ||
        (r.customer_part_no ?? "").toLowerCase().includes(it)
      )) return false;
      if (ep && !r.epc.toLowerCase().includes(ep)) return false;
      if (confidenceFilter && r.confidence !== confidenceFilter) return false;
      return true;
    });
  }, [rows, searchFilter, eventFilter, itemFilter, epcFilter, confidenceFilter]);

  const openDetail = async (ticketId: number) => {
    if (!token) return;
    setDetailLoadingId(ticketId);
    setError(null);
    try {
      const d = await fetchDeparture(token, ticketId);
      setDetail(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDetailLoadingId(null);
    }
  };

  const hasNext = offset + LIMIT < total;
  const hasPrev = offset > 0;

  const headerCount = useMemo(() => {
    if (loading && rows.length === 0) return "loading…";
    if (total === 0) return "0";
    // When the operator has typed a search, surface the matching subset
    // count alongside the loaded total. Otherwise just show the loaded
    // window of the server total.
    if (searchFilter.trim()) {
      return `${visibleRows.length.toLocaleString()} match · ${total.toLocaleString()} loaded`;
    }
    const from = offset + 1;
    const to = Math.min(offset + rows.length, total);
    return `${from}–${to} of ${total.toLocaleString()}`;
  }, [loading, rows.length, total, offset, searchFilter, visibleRows.length]);

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h1 className="text-xl font-bold">Departures</h1>
          <p className="text-xs text-secondary-500 mt-0.5">
            Confirmed tag-take transactions from camera person-events. One row per EPC.
          </p>
        </div>
        <div className="text-xs text-secondary-500">{headerCount}</div>
      </div>

      {/* Filters */}
      <div className="bg-secondary-50 border border-secondary-200 rounded-md p-3 mb-3 space-y-2">
        <label className="block text-xs text-secondary-600">
          <div className="flex items-center justify-between">
            <span>EPC search (full history, ignores date range)</span>
            {epcSearch && (
              <button
                type="button"
                onClick={() => setEpcSearch("")}
                className="text-[10px] text-primary-600 hover:underline"
              >
                clear
              </button>
            )}
          </div>
          <input
            type="text"
            value={epcSearch}
            onChange={(e) => setEpcSearch(e.target.value)}
            placeholder="paste or scan an EPC to find every event for that tag"
            className="w-full border border-secondary-300 rounded px-2 py-1 text-sm font-mono"
          />
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <label className="text-xs text-secondary-600">
            <div>Smartbox / Work center</div>
            <input
              type="text"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="SB071, X24, …"
              className="w-full border border-secondary-300 rounded px-2 py-1 text-sm"
            />
          </label>
          <label className="text-xs text-secondary-600">
            <div>Since {epcSearch && <span className="text-secondary-400">(EPC search ignores)</span>}</div>
            <input
              type="date"
              value={sinceFilter}
              onChange={(e) => setSinceFilter(e.target.value)}
              disabled={!!epcSearch}
              className="w-full border border-secondary-300 rounded px-2 py-1 text-sm disabled:bg-secondary-100"
            />
          </label>
          <label className="text-xs text-secondary-600">
            <div>Until {epcSearch && <span className="text-secondary-400">(EPC search ignores)</span>}</div>
            <input
              type="date"
              value={untilFilter}
              onChange={(e) => setUntilFilter(e.target.value)}
              disabled={!!epcSearch}
              className="w-full border border-secondary-300 rounded px-2 py-1 text-sm disabled:bg-secondary-100"
            />
          </label>
        </div>
      </div>

      {error && (
        <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded mb-3">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-secondary-50 text-secondary-700 uppercase">
            <tr>
              <th className="px-2 py-2 w-12" aria-label="Episode photo"></th>
              <th className="text-left px-3 py-2">Event</th>
              <th className="text-left px-3 py-2">SmartBox</th>
              <th className="text-left px-3 py-2">Item</th>
              <th className="text-left px-3 py-2">EPC</th>
              {showPricing && (
                <th
                  className="text-right px-3 py-2"
                  title="Snapshotted at CONFIRMED time — future price changes don't affect billed events."
                >
                  Price
                </th>
              )}
              <th
                className="text-left px-3 py-2"
                title="Rendered in the smartbox's local time (Central by default)"
              >
                Timestamp
              </th>
              {showConfidence && <th className="text-left px-3 py-2">Confidence</th>}
              <th className="text-right px-3 py-2"></th>
            </tr>
            {/* Per-column refinement row. SmartBox + Timestamp omitted
                — the top-bar search and date range already cover them. */}
            <tr className="bg-white border-t border-secondary-200 normal-case">
              <th className="px-2 pb-1.5"></th>
              <th className="px-2 pb-1.5 font-normal">
                <input
                  type="text"
                  value={eventFilter}
                  onChange={(e) => setEventFilter(e.target.value)}
                  placeholder="filter"
                  className="w-full border border-secondary-300 rounded px-1.5 py-0.5 text-xs"
                />
              </th>
              <th className="px-2 pb-1.5"></th>
              <th className="px-2 pb-1.5 font-normal">
                <input
                  type="text"
                  value={itemFilter}
                  onChange={(e) => setItemFilter(e.target.value)}
                  placeholder="sku / name / vendor / custpart"
                  className="w-full border border-secondary-300 rounded px-1.5 py-0.5 text-xs"
                />
              </th>
              <th className="px-2 pb-1.5 font-normal">
                <input
                  type="text"
                  value={epcFilter}
                  onChange={(e) => setEpcFilter(e.target.value)}
                  placeholder="filter"
                  className="w-full border border-secondary-300 rounded px-1.5 py-0.5 text-xs font-mono"
                />
              </th>
              {showPricing && <th className="px-2 pb-1.5"></th>}
              <th className="px-2 pb-1.5"></th>
              {showConfidence && (
                <th className="px-2 pb-1.5 font-normal">
                  <select
                    value={confidenceFilter}
                    onChange={(e) => setConfidenceFilter(e.target.value as "" | DepartureConfidence)}
                    className="w-full border border-secondary-300 rounded px-1.5 py-0.5 text-xs bg-white normal-case"
                  >
                    <option value="">All</option>
                    <option value="HIGH">HIGH</option>
                    <option value="MEDIUM">MEDIUM</option>
                    <option value="LOW">LOW</option>
                  </select>
                </th>
              )}
              <th className="px-2 pb-1.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-100">
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={(showConfidence ? 10 : 9) - (showPricing ? 0 : 1)} className="px-3 py-6 text-center text-secondary-500">
                  loading…
                </td>
              </tr>
            ) : visibleRows.length === 0 ? (
              <tr>
                <td colSpan={(showConfidence ? 10 : 9) - (showPricing ? 0 : 1)} className="px-3 py-6 text-center text-secondary-500">
                  No confirmed departures match the current filters.
                </td>
              </tr>
            ) : (
              visibleRows.map((r) => {
                // is_cancelled: the customer has cancellation enabled
                // and this row's pair (Departure ↔ Re-arrival) fell in
                // the same week-window. Strikethrough the substance
                // (item / EPC) so operators see the audit trail with
                // an unambiguous "this one nets out" cue.
                const c = r.is_cancelled;
                const rowTint = c ? "bg-error-50/30 hover:bg-error-50" : "hover:bg-secondary-50";
                const strike = c ? "line-through text-secondary-500" : "";
                return (
                <tr key={r.departure_ticket_epc_id} className={`${rowTint} align-top`}>
                  <td className="px-2 py-2">
                    {r.thumbnail_url ? (
                      <button
                        type="button"
                        onClick={() => setEpisodeView(r)}
                        className="block w-12 h-12 rounded overflow-hidden border border-secondary-200 hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                        title="Click to view the camera-episode photos"
                      >
                        <img
                          src={r.thumbnail_url}
                          alt=""
                          loading="lazy"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // If Azure blob 404s (rare), hide the broken
                            // image so the row still flows.
                            (e.target as HTMLImageElement).style.visibility = "hidden";
                          }}
                        />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEpisodeView(r)}
                        className="block w-12 h-12 rounded border border-dashed border-secondary-200 text-secondary-400 text-[10px] flex items-center justify-center hover:border-primary-400"
                        title="Open episode photos (no preview frame available)"
                      >
                        photos
                      </button>
                    )}
                  </td>
                  <td
                    className="px-3 py-2 font-mono text-secondary-700"
                    title={
                      c
                        ? `Cancelled by RE_ARRIVAL #${r.paired_event_id ?? "?"} (same-week reversal)`
                        : "Camera person-event id"
                    }
                  >
                    {c && (
                      <span className="text-error-700 mr-1 text-[10px]" title="Reversed">⊘</span>
                    )}
                    #{r.camera_person_episode_id}
                  </td>
                  <td className="px-3 py-2 font-medium">
                    {r.smartbox_code ?? "—"}
                    {r.work_center && (
                      <span className="ml-1.5 text-secondary-600 font-normal">
                        · {fmtWorkCenter(r.work_center)}
                      </span>
                    )}
                    <div
                      className="text-[10px] text-secondary-500 font-mono"
                      title={r.scu_name}
                    >
                      {shortScuName(r.scu_name)}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className={"font-mono " + strike}>
                      {r.sku ?? (r.item_id ? `#${r.item_id}` : "—")}
                    </div>
                    {r.customer_part_no && (
                      <div className={"text-[10px] font-mono " + (c ? "line-through text-secondary-400" : "text-secondary-600")}
                           title="Customer's part number (custpart on the consignment report)">
                        cust {r.customer_part_no}
                      </div>
                    )}
                    {r.item_name && (
                      <div className={"text-[10px] " + (c ? "line-through text-secondary-400" : "text-secondary-500")}>
                        {r.item_name}
                      </div>
                    )}
                    {r.vendor_name && (
                      <div className="text-[10px] text-secondary-500">{r.vendor_name}</div>
                    )}
                  </td>
                  <td className={"px-3 py-2 font-mono " + strike}>
                    {r.epc}
                  </td>
                  {showPricing && (
                    <td className={"px-3 py-2 text-right font-semibold " + strike}>
                      {fmtMoney(r.unit_price, r.currency)}
                    </td>
                  )}
                  <td className="px-3 py-2 text-secondary-700">
                    {(() => {
                      const ts = r.confirmed_at ?? r.episode_end_utc;
                      const c = scuCoords.get(r.scu_name);
                      const tz = tzFromLatLon(c?.[0], c?.[1]);
                      return (
                        <>
                          {fmtUtcInTz(ts, tz)}
                          {ts && (
                            <div className="text-[10px] text-secondary-500">
                              {fmtAgo(ts)} ago
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </td>
                  {showConfidence && (
                    <td className="px-3 py-2">
                      {r.confidence ? (
                        <span
                          className={
                            "inline-flex items-center rounded px-1.5 py-0.5 " +
                            CONFIDENCE_PILL[r.confidence]
                          }
                        >
                          {r.confidence}
                        </span>
                      ) : (
                        "—"
                      )}
                      {(r.pre_read_count != null || r.post_miss_count != null) && (
                        <div className="text-[10px] text-secondary-500">
                          {r.pre_read_count ?? "—"} pre / {r.post_miss_count ?? "—"} miss
                        </div>
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void openDetail(r.departure_ticket_id)}
                      disabled={detailLoadingId === r.departure_ticket_id}
                    >
                      {detailLoadingId === r.departure_ticket_id ? "…" : "Ticket"}
                    </Button>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > LIMIT && (
        <div className="mt-3 flex items-center justify-between text-xs">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            disabled={!hasPrev || loading}
          >
            ← Prev
          </Button>
          <div className="text-secondary-500">{headerCount}</div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOffset(offset + LIMIT)}
            disabled={!hasNext || loading}
          >
            Next →
          </Button>
        </div>
      )}

      {/* Episode photo modal — opened by clicking a row's thumbnail.
          Independent of the ticket-detail modal below; you can have
          one or the other open but not both at once (the close
          buttons reset their own state). */}
      {episodeView && episodeView.episode_start_utc && (
        <DepartureEpisodeModal
          scu={episodeView.scu_name}
          episodeId={episodeView.camera_person_episode_id}
          episodeStartUtc={episodeView.episode_start_utc}
          episodeEndUtc={episodeView.episode_end_utc}
          smartboxCode={episodeView.smartbox_code}
          itemLabel={episodeView.sku ?? (episodeView.item_id ? `#${episodeView.item_id}` : null)}
          onClose={() => setEpisodeView(null)}
        />
      )}

      {/* Drill-down: parent ticket detail (same modal as before — the
          ticket-shaped /api/departures/{id} endpoint is unchanged). */}
      {detail && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setDetail(null)}
        >
          <div
            className="bg-white rounded-md shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-secondary-200 px-5 py-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">
                  {(detail.ticket as { smartbox_code?: string | null }).smartbox_code ?? "—"}
                  {(detail.ticket as { work_center?: string | null }).work_center && (
                    <span className="ml-2 text-secondary-600 font-normal">
                      · {fmtWorkCenter((detail.ticket as { work_center?: string | null }).work_center)}
                    </span>
                  )}
                  <span className="ml-2 text-secondary-500 font-normal">
                    #{detail.ticket.departure_ticket_id}
                  </span>
                </div>
                <div className="text-xs text-secondary-500">
                  <span className="font-mono" title={detail.ticket.scu_name}>
                    {shortScuName(detail.ticket.scu_name)}
                  </span>
                  {" · "}
                  Episode {fmtUtc(detail.ticket.episode_start_utc)} →{" "}
                  {fmtUtc(detail.ticket.episode_end_utc)} · {detail.ticket.photo_count ?? 0} photos
                </div>
              </div>
              <span
                className={
                  "inline-flex items-center rounded px-1.5 py-0.5 text-xs " +
                  STATUS_PILL[detail.ticket.status as DepartureStatus]
                }
              >
                {detail.ticket.status}
              </span>
            </div>

            <div className="px-5 py-4">
              <div className="text-xs text-secondary-600 mb-2">
                EPC lines on this ticket
                {detail.epc_lines.length === 0 && (
                  <span className="ml-2 italic text-secondary-500">
                    (none in scope)
                  </span>
                )}
              </div>
              {detail.epc_lines.length > 0 && (
                <table className="min-w-full text-xs">
                  <thead className="bg-secondary-50 text-secondary-700 uppercase">
                    <tr>
                      <th className="text-left px-2 py-1">EPC</th>
                      <th className="text-left px-2 py-1">Line</th>
                      <th className="text-left px-2 py-1">Pre / Post</th>
                      <th className="text-left px-2 py-1">Confidence</th>
                      <th className="text-left px-2 py-1">Last read</th>
                      <th className="text-left px-2 py-1">Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100">
                    {detail.epc_lines.map((e) => (
                      <tr key={e.departure_ticket_epc_id}>
                        <td className="px-2 py-1 font-mono">{e.epc}</td>
                        <td className="px-2 py-1">
                          <span
                            className={
                              "inline-flex items-center rounded px-1.5 py-0.5 " +
                              (e.line_status === "CONFIRMED"
                                ? STATUS_PILL.CONFIRMED
                                : STATUS_PILL.VOID)
                            }
                          >
                            {e.line_status}
                          </span>
                        </td>
                        <td className="px-2 py-1 text-secondary-700">
                          {e.pre_read_count ?? "—"} / {e.post_miss_count ?? "—"}
                        </td>
                        <td className="px-2 py-1">
                          {e.confidence && (
                            <span
                              className={
                                "inline-flex items-center rounded px-1.5 py-0.5 " +
                                (CONFIDENCE_PILL[e.confidence as DepartureConfidence] ?? "")
                              }
                            >
                              {e.confidence}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1 text-secondary-700">
                          {fmtUtc(e.last_read_utc)}
                        </td>
                        <td className="px-2 py-1 text-secondary-500">
                          {e.void_reason || ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="border-t border-secondary-200 px-5 py-3 flex justify-end">
              <Button onClick={() => setDetail(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeparturesPage;
